import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { UserPlus, Upload, FileUp, Check, X } from 'lucide-react';

const StaffCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [manualLead, setManualLead] = useState({ name: '', number: '' });
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkStats, setBulkStats] = useState(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const response = await api.getStaffCampaigns();
      setCampaigns(response.campaigns);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleManualLead = async (e) => {
    e.preventDefault();
    try {
      await api.addManualLead({
        campaign_id: selectedCampaign.id,
        name: manualLead.name,
        number: manualLead.number
      });
      toast.success('Lead added successfully');
      setShowManualDialog(false);
      setManualLead({ name: '', number: '' });
      loadCampaigns();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      const stats = await api.uploadBulkLeads(selectedCampaign.id, bulkFile);
      setBulkStats(stats);
      setShowBulkDialog(false);
      setShowConfirmDialog(true);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleConfirmBulk = async () => {
    try {
      await api.confirmBulkLeads(selectedCampaign.id, bulkStats.leads_to_add);
      toast.success(`${bulkStats.total_valid} leads added successfully`);
      setShowConfirmDialog(false);
      setBulkStats(null);
      setBulkFile(null);
      loadCampaigns();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openManualDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setShowManualDialog(true);
  };

  const openBulkDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setShowBulkDialog(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-slate-900" data-testid="staff-campaigns-title">My Campaigns</h2>
        <p className="text-slate-600">Manage your assigned campaigns and leads</p>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading campaigns...</div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-500">No campaigns assigned to you yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className="card-hover" data-testid={`campaign-card-${campaign.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{campaign.title}</CardTitle>
                    <CardDescription>Campaign ID: {campaign.id.substring(0, 8)}...</CardDescription>
                  </div>
                  <Badge data-testid={`campaign-lead-count-${campaign.id}`}>
                    {campaign.lead_count} Leads
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => openManualDialog(campaign)}
                    data-testid={`manual-add-${campaign.id}`}
                  >
                    <UserPlus className="w-4 h-4 mr-2" /> Add Manual Lead
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => openBulkDialog(campaign)}
                    data-testid={`bulk-add-${campaign.id}`}
                  >
                    <Upload className="w-4 h-4 mr-2" /> Bulk Upload
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Manual Lead Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent data-testid="manual-lead-dialog">
          <DialogHeader>
            <DialogTitle>Add Manual Lead</DialogTitle>
            <DialogDescription>
              Add a single lead to {selectedCampaign?.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleManualLead} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-testid="lead-name-input"
                value={manualLead.name}
                onChange={(e) => setManualLead({ ...manualLead, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="number">Phone Number (with country code)</Label>
              <Input
                id="number"
                placeholder="+919876543210"
                data-testid="lead-number-input"
                value={manualLead.number}
                onChange={(e) => setManualLead({ ...manualLead, number: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowManualDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-manual-lead">
                Add Lead
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Leads</DialogTitle>
            <DialogDescription>
              Upload CSV file (Max 500 leads). Format: Name,Number
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkUpload} className="space-y-4">
            <div>
              <Label htmlFor="file">CSV File</Label>
              <Input
                id="file"
                type="file"
                accept=".csv"
                data-testid="bulk-file-input"
                onChange={(e) => setBulkFile(e.target.files[0])}
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                CSV must have headers: Name,Number
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowBulkDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="upload-bulk-leads">
                <FileUp className="w-4 h-4 mr-2" /> Upload
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Bulk Upload</DialogTitle>
            <DialogDescription>Review the upload statistics</DialogDescription>
          </DialogHeader>
          {bulkStats && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-slate-600">Total Uploaded</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="total-uploaded">{bulkStats.total_uploaded}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-slate-600">Valid Leads</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="total-valid">{bulkStats.total_valid}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-sm text-slate-600">Invalid</p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="total-invalid">{bulkStats.total_invalid}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-slate-600">Duplicates</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="total-duplicates">
                    {bulkStats.duplicate_in_upload + bulkStats.duplicate_in_csv}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1"
                  data-testid="decline-bulk-upload"
                >
                  <X className="w-4 h-4 mr-2" /> Decline
                </Button>
                <Button
                  onClick={handleConfirmBulk}
                  className="flex-1"
                  data-testid="confirm-bulk-upload"
                >
                  <Check className="w-4 h-4 mr-2" /> Confirm ({bulkStats.total_valid})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffCampaigns;